import type { AnalyticsProvider, ObservabilityProvider } from './types';
import { noopProvider } from './providers/noop';
import type { EmitEvent } from './events';
// Type-only import — erased at compile time, so it does NOT pull `web.ts`
// (or its `@datadog/browser-logs` dependency) into the runtime module graph.
import type { WebProviderConfig } from './providers/web';
// Type-only — erased at compile time, so it does NOT pull `mobile.native.ts`
// (or its `@datadog/mobile-react-native` dependency) into the runtime graph.
import type { MobileProviderConfig } from './providers/mobile.native';
import { isAnalyticsSubjectId } from './analytics-resolve';

export type { WebProviderConfig, MobileProviderConfig };

type ProviderName = 'noop' | 'web' | 'node' | 'mobile';

export interface InitObservabilityOptions {
  /** Logs/metrics/traces bundle selector (OBSERVABILITY_PROVIDER): `noop` | `web` | `node` | `mobile`. */
  provider?: string;
  config?: WebProviderConfig | MobileProviderConfig;
  /**
   * Pre-built analytics concrete for the `analytics` slot. Analytics is opt-in
   * per app: a consumer that wants PostHog imports its platform concrete from
   * `@galapagos/observability/posthog/{web,native,node}` and passes the result
   * here. Apps that omit it keep the bundle's `noop` analytics — and never pull
   * a PostHog SDK into their bundle graph. The slot composes independently of the
   * logs/metrics/traces bundle selected by `provider`.
   */
  analytics?: AnalyticsProvider;
  /**
   * Pre-built server bundle concrete for the `node` provider (the Datadog
   * server logs/metrics/traces SDKs). Like `analytics`, the concrete is imported
   * by the server app from `@galapagos/observability/lambda` and passed here —
   * NEVER imported by this factory — so a web/native bundler never pulls the
   * server SDKs (and their transitive `@aws-sdk` deps) into its graph.
   * `provider: 'node'` without it degrades to noop (fail-safe).
   */
  nodeProvider?: () => ObservabilityProvider;
  /**
   * Pre-built mobile bundle concrete for the `mobile` provider (the Datadog RN
   * SDK logs/metrics/traces). Like `nodeProvider`, the concrete is imported by
   * the mobile app from `@galapagos/observability/mobile-rum` and passed here —
   * NEVER imported by this factory — so a web/node (esbuild) bundler never pulls
   * `@datadog/mobile-react-native` (and its `react-native` Flow-syntax deps) into
   * its graph. `provider: 'mobile'` without it degrades to noop (fail-safe).
   */
  mobileProvider?: () => ObservabilityProvider;
  /**
   * Optional async pre-step awaited INSIDE the readiness promise before the
   * provider is installed (used by the mobile app for `initMobileSdk`). Because
   * `initObservability` assigns `_ready` synchronously, threading the native SDK
   * init through here — rather than `await`-ing it before calling
   * `initObservability` — keeps `analyticsReady()` pending until the SDK is up, so
   * screen/view tracking can't fire against the default resolved promise mid-init.
   */
  prepare?: () => Promise<unknown>;
}

let _provider: ObservabilityProvider | null = null;
let _ready: Promise<void> = Promise.resolve();
// Bumped by every `initObservability` call; `_settledGeneration` catches up
// when that call's init settles. Equal means no init is in flight.
let _initGeneration = 0;
let _settledGeneration = 0;

/**
 * Synchronous getter — returns the registered provider, defaulting to `noop`.
 * It never reads the environment or throws; selection (and its fail-fast
 * validation) lives in `initObservability`, the async configuration entry.
 */
export function getProvider(): ObservabilityProvider {
  if (!_provider) {
    _provider = noopProvider;
  }
  return _provider;
}

/**
 * Resolves once the most recent `initObservability` call has settled (or
 * immediately if none is in flight). Lets callers fire the first event *after*
 * the configured provider is installed, instead of racing the async init and
 * losing it to the `noop` default — e.g. the first page_view / screen_view.
 */
export function analyticsReady(): Promise<void> {
  return _ready;
}

/**
 * Whether the most recent `initObservability` call has settled, so an event
 * captured now reaches the configured provider rather than the pre-init noop.
 *
 * The synchronous counterpart of `analyticsReady()`, for a caller that must
 * capture in the same tick as the user's activation: a click that navigates
 * synchronously would otherwise be captured after the navigation, against the
 * next page's URL.
 */
export function analyticsSettled(): boolean {
  return _settledGeneration === _initGeneration;
}

function resolveSelection(explicit?: string): ProviderName {
  const raw =
    explicit ?? (typeof process !== 'undefined' ? process.env?.OBSERVABILITY_PROVIDER : undefined);
  if (!raw || raw === 'noop') return 'noop';
  if (raw === 'web') return 'web';
  if (raw === 'node') return 'node';
  if (raw === 'mobile') return 'mobile';
  // ADR-053 Rule 4: an unrecognised selector fails fast rather than silently
  // masking a misconfiguration by falling through to the default.
  throw new Error(
    `Unknown observability provider "${raw}" — expected "noop", "web", "node", or "mobile" (OBSERVABILITY_PROVIDER).`,
  );
}

// One-time warning latch for a known-but-unavailable provider degrading to noop.
let _warnedDegraded = false;

/**
 * Enforce the analytics identity boundary at the final seam: only the raw
 * canonical member UUID may reach a sink (ADR-069); email, phone, and display
 * names — and any non-UUID string — remain invalid distinct ids.
 */
function withIdentityGuard(
  analytics: AnalyticsProvider,
  logs: ObservabilityProvider['logs'],
): AnalyticsProvider {
  return {
    ...analytics,
    setUser: (userId: string, properties?: Record<string, unknown>) => {
      if (isAnalyticsSubjectId(userId)) {
        analytics.setUser(userId, properties);
        return;
      }
      logs.log('debug', 'Dropped analytics setUser for invalid analytics identity', {
        reason: 'invalid_analytics_identity_shape',
      });
    },
  };
}

/**
 * Configure the active provider. The logs/metrics/traces bundle is selected by
 * `opts.provider` (`OBSERVABILITY_PROVIDER`); the analytics slot is composed
 * independently from an optional pre-built `opts.analytics` concrete (opt-in per
 * app). Selecting a PostHog analytics concrete overrides only the `analytics`
 * slot, leaving a `web` (Datadog) logs bundle intact — and vice-versa. The web
 * bundle concrete is reached via a dynamic `import()` so the default path loads
 * no vendor bundle; PostHog concretes are imported by the app, never here.
 */
export function initObservability(opts: InitObservabilityOptions = {}): Promise<void> {
  // Assign `_ready` synchronously (before the first await) so `analyticsReady()`
  // observes this in-flight init even when called in the same tick.
  const generation = ++_initGeneration;
  const markSettled = () => {
    if (_initGeneration === generation) _settledGeneration = generation;
  };
  _ready = _initObservability(opts);
  _ready.then(markSettled, markSettled);
  return _ready;
}

async function _initObservability(opts: InitObservabilityOptions): Promise<void> {
  const bundleSelection = resolveSelection(opts.provider);
  let base: ObservabilityProvider;
  if (bundleSelection === 'noop') {
    base = noopProvider;
  } else if (bundleSelection === 'web') {
    if (!opts.config) {
      throw new Error('initObservability: the "web" provider requires a config object.');
    }
    const { createWebProvider } = await import('./providers/web');
    base = createWebProvider(opts.config);
  } else if (bundleSelection === 'mobile') {
    // The RN SDK concrete is INJECTED by the mobile app (`opts.mobileProvider`,
    // from `@galapagos/observability/mobile-rum`), never imported here — so a
    // web/node (esbuild) bundler never pulls `@datadog/mobile-react-native` (and
    // its `react-native` Flow-syntax deps) into its graph. Mirrors the node +
    // PostHog injection patterns. Fail-safe (ADR-068 Rule 6): a missing injection
    // or a failing concrete degrades to noop + a one-time warning.
    try {
      if (!opts.mobileProvider) {
        throw new Error(
          'the "mobile" provider requires opts.mobileProvider — inject createMobileProvider from @galapagos/observability/mobile-rum',
        );
      }
      // Await the native SDK init (opts.prepare = initMobileSdk) HERE, inside the
      // readiness promise, so `analyticsReady()` stays pending until the SDK is up
      // and the provider is installed — otherwise consumers gating on
      // analyticsReady() (screen/view tracking) fire against the default resolved
      // promise while init is in flight and lose the first event.
      if (opts.prepare) {
        const prepared = await opts.prepare();
        if (prepared === false) {
          throw new Error('mobile provider preparation reported unavailable');
        }
      }
      base = opts.mobileProvider();
    } catch (err) {
      if (!_warnedDegraded) {
        _warnedDegraded = true;
        console.warn(
          `[observability] "mobile" provider unavailable — degrading to noop. ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
      }
      base = noopProvider;
    }
  } else {
    // 'node' — the concrete is INJECTED by the server app (`opts.nodeProvider`,
    // from `@galapagos/observability/lambda`), never imported here, so no server
    // SDK is reachable from this factory in a web/native bundle graph (mirrors
    // the PostHog analytics pattern). Fail-safe (ADR-068 Rule 6): a missing
    // injection or a concrete whose SDKs are unavailable degrades to noop + a
    // one-time warning rather than crashing boot.
    try {
      if (!opts.nodeProvider) {
        throw new Error(
          'the "node" provider requires opts.nodeProvider — inject createNodeProvider from @galapagos/observability/lambda',
        );
      }
      base = opts.nodeProvider();
    } catch (err) {
      if (!_warnedDegraded) {
        _warnedDegraded = true;
        console.warn(
          `[observability] "node" provider unavailable — degrading to noop. ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
      }
      base = noopProvider;
    }
  }

  // Guard the FINAL analytics slot — the injected concrete when present, else the
  // base bundle's own analytics — so the identity boundary holds on every path,
  // including `provider: 'web'` with no injected concrete (Datadog `setUser`).
  // The noop analytics is already inert, so leave the pure-noop provider untouched
  // (preserves its by-identity inertness) rather than wrapping a no-op in a guard.
  const analytics = opts.analytics ?? base.analytics;
  _provider =
    analytics === noopProvider.analytics
      ? base
      : { ...base, analytics: withIdentityGuard(analytics, base.logs) };
}

export function emit(event: EmitEvent): void {
  const provider = getProvider();
  const timestamp = event.timestamp ?? new Date().toISOString();

  provider.logs.log('info', event.event, { ...event, timestamp });

  const tags: Record<string, string> = {
    event: event.event,
    ...(event.skill ? { skill: event.skill } : {}),
    ...(event.ticket ? { ticket: event.ticket } : {}),
    ...(event.status ? { status: event.status } : {}),
  };
  provider.metrics.increment('harness_event_total', tags);

  if (event.duration_ms !== undefined) {
    provider.metrics.histogram('harness_duration_ms', event.duration_ms, tags);
  }
}
